// src/pages/admin/AdminDashboard.tsx
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import { GlobalStyles } from "../../styles/globalStyles";
import {
  Users,
  Shield,
  FileText,
  CreditCard,
  TrendingUp,
  Clock,
} from "lucide-react";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";

const AdminDashboard: React.FC = () => {
  const { user } = useSelector((state: RootState) => state.auth);
  // eslint-disable-next-line
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    pendingKyc: 0,
    totalTransactions: 0,
    pendingDeposits: 0,
    pendingWithdrawals: 0,
    totalRecoveryCases: 0,
    activeRecoveryCases: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      // Fetch data from APIs
      // ... existing data fetching logic ...
    } catch (error) {
      console.error("Failed to fetch dashboard stats:", error);
      toast.error("Failed to load dashboard statistics");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className={`${GlobalStyles.spinner} h-8 w-8`}></div>
      </div>
    );
  }

  return (
    <div className={GlobalStyles.pageContainer}>
      <div className={GlobalStyles.contentContainer}>
        {/* Header */}
        <div className={GlobalStyles.sectionSpacing}>
          <h1 className={GlobalStyles.heading1}>Admin Dashboard</h1>
          <p className={GlobalStyles.bodyText + " mt-2"}>
            Welcome back, Admin {user?.first_name}. Here's your platform
            overview.
          </p>
        </div>

        {/* Stats Grid */}
        <div
          className={`${GlobalStyles.gridCols4} gap-6 ${GlobalStyles.sectionSpacing}`}
        >
          {/* Users Card */}
          <Link to="/admin/users" className={GlobalStyles.cardHover}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Total Users
                </p>
                <p className={`${GlobalStyles.heading2} mt-1`}>
                  {stats.totalUsers}
                </p>
              </div>
              <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/20">
                <Users className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
            <div className="flex items-center mt-4">
              <TrendingUp className="h-4 w-4 text-green-500 mr-1" />
              <span className="text-sm text-green-600 dark:text-green-400">
                {stats.activeUsers} active today
              </span>
            </div>
          </Link>

          {/* KYC Card */}
          <Link to="/admin/kyc" className={GlobalStyles.cardHover}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Pending KYC
                </p>
                <p className={`${GlobalStyles.heading2} mt-1`}>
                  {stats.pendingKyc}
                </p>
              </div>
              <div className="p-2 rounded-lg bg-yellow-100 dark:bg-yellow-900/20">
                <Shield className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />
              </div>
            </div>
            <div className="mt-4">
              <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <div
                  className="h-full bg-yellow-500"
                  style={{ width: `${Math.min(stats.pendingKyc * 10, 100)}%` }}
                ></div>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {stats.pendingKyc} to review
              </p>
            </div>
          </Link>

          {/* Transactions Card */}
          <Link to="/admin/transactions" className={GlobalStyles.cardHover}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Total Transactions
                </p>
                <p className={`${GlobalStyles.heading2} mt-1`}>
                  {stats.totalTransactions}
                </p>
              </div>
              <div className="p-2 rounded-lg bg-green-100 dark:bg-green-900/20">
                <CreditCard className="h-6 w-6 text-green-600 dark:text-green-400" />
              </div>
            </div>
            <div className="flex items-center mt-4">
              <Clock className="h-4 w-4 text-yellow-500 mr-1" />
              <span className="text-sm text-yellow-600 dark:text-yellow-400">
                {stats.pendingDeposits + stats.pendingWithdrawals} pending
              </span>
            </div>
          </Link>

          {/* Recovery Card */}
          <Link to="/admin/recovery" className={GlobalStyles.cardHover}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Active Recovery
                </p>
                <p className={`${GlobalStyles.heading2} mt-1`}>
                  {stats.activeRecoveryCases}
                </p>
              </div>
              <div className="p-2 rounded-lg bg-purple-100 dark:bg-purple-900/20">
                <FileText className="h-6 w-6 text-purple-600 dark:text-purple-400" />
              </div>
            </div>
            <div className="mt-4">
              <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <div
                  className="h-full bg-purple-500"
                  style={{
                    width: `${Math.min(stats.activeRecoveryCases * 20, 100)}%`,
                  }}
                ></div>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {stats.activeRecoveryCases} active cases
              </p>
            </div>
          </Link>
        </div>

        {/* Quick Actions */}
        <div className={`${GlobalStyles.card} ${GlobalStyles.sectionSpacing}`}>
          <h3 className={`${GlobalStyles.heading2} mb-6`}>Quick Actions</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Link
              to="/admin/users"
              className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} w-full justify-start`}
            >
              <Users className="h-4 w-4 mr-2" />
              Manage Users
            </Link>
            <Link
              to="/admin/kyc"
              className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} w-full justify-start`}
            >
              <Shield className="h-4 w-4 mr-2" />
              Review KYC
            </Link>
            <Link
              to="/admin/recovery"
              className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} w-full justify-start`}
            >
              <FileText className="h-4 w-4 mr-2" />
              Recovery Cases
            </Link>
            <Link
              to="/admin/transactions"
              className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} w-full justify-start`}
            >
              <CreditCard className="h-4 w-4 mr-2" />
              Transactions
            </Link>
          </div>
        </div>

        {/* Pending Actions */}
        <div className={GlobalStyles.card}>
          <h3 className={`${GlobalStyles.heading2} mb-6`}>Pending Actions</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <span className="font-medium text-gray-900 dark:text-white">
                  Deposits to approve
                </span>
                <span
                  className={`${GlobalStyles.badge} ${GlobalStyles.badgeWarning}`}
                >
                  {stats.pendingDeposits}
                </span>
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Pending deposit approvals
              </p>
            </div>
            <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <span className="font-medium text-gray-900 dark:text-white">
                  Withdrawals to process
                </span>
                <span
                  className={`${GlobalStyles.badge} ${GlobalStyles.badgeWarning}`}
                >
                  {stats.pendingWithdrawals}
                </span>
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Pending withdrawal requests
              </p>
            </div>
            <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <span className="font-medium text-gray-900 dark:text-white">
                  KYC to review
                </span>
                <span
                  className={`${GlobalStyles.badge} ${GlobalStyles.badgeWarning}`}
                >
                  {stats.pendingKyc}
                </span>
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Pending KYC submissions
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
